import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Button, Chip, Tooltip,
  Stack, Avatar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FileDownload as ExportIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import useAxiosPrivate from '../../Hooks/useAxiosPrivate';

const resolvePhoto = (raw) => {
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  const base = process.env.REACT_APP_API_URL ||
    (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
  return `${base}${raw}`;
};

// ─── Export helpers ──────────────────────────────────────────────────────────

function rowsToCsv(rows, columns) {
  const headers = columns.map(c => `"${String(c.headerName || c.field).replace(/"/g, '""')}"`).join(',');
  const lines   = rows.map(row =>
    columns.map(c => {
      const val = c.exportValue ? c.exportValue(row) : (row[c.field] ?? '');
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(',')
  );
  return [headers, ...lines].join('\r\n');
}

function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportCsv(rows, columns, filename) {
  downloadBlob(rowsToCsv(rows, columns), filename, 'text/csv;charset=utf-8;');
}

function exportExcel(rows, columns, filename) {
  const headers = columns.map(c => `<th>${String(c.headerName || c.field)}</th>`).join('');
  const body    = rows.map(row =>
    `<tr>${columns.map(c => {
      const val = c.exportValue ? c.exportValue(row) : (row[c.field] ?? '');
      return `<td>${String(val)}</td>`;
    }).join('')}</tr>`
  ).join('');
  const html = `<table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table>`;
  downloadBlob(html, filename, 'application/vnd.ms-excel');
}

// ─── Stat card ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value }) => (
  <Paper elevation={0} sx={{
    flex: 1, minWidth: 140, p: 2.5, borderRadius: 2,
    border: '1px solid #e0e0e0', bgcolor: '#fff'
  }}>
    <Typography variant="caption" color="text.secondary"
      sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: .5 }}>
      {label}
    </Typography>
    <Typography variant="h3" fontWeight={700} mt={0.5}>
      {value ?? '–'}
    </Typography>
  </Paper>
);

// ─── Badge chips ─────────────────────────────────────────────────────────────

const YesChip = () => (
  <Chip icon={<CheckCircleIcon />} label="Yes" color="success" size="small" variant="outlined" />
);
const NoChip = () => (
  <Chip icon={<CancelIcon />} label="No" color="error" size="small" variant="outlined" />
);
const PAYMENT_STATUS_COLORS = {
  'New':              'default',
  'Partial Payment':  'warning',
  'Payment Received': 'success',
  'Refunded':         'info',
  'Cancelled':        'error',
};

const formatBalance = (balance) => {
  const n = Number(balance) || 0;
  if(n == 0){
    return " - ";
  }
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const PaymentChip = ({ status, balance }) => {
  const label = status || 'New';
  const color = PAYMENT_STATUS_COLORS[label] || 'default';
  return (
    <Tooltip title={label} arrow>
      <Chip
        label={formatBalance(balance)}
        color={color}
        size="small"
        variant="filled"
      />
    </Tooltip>
  );
};

// ─── Export column definitions ───────────────────────────────────────────────

const EXPORT_COLS = [
  { field: 'name',             headerName: 'Name' },
  { field: 'registrationType', headerName: 'Registration Type' },
  { field: 'email',            headerName: 'Email' },
  { field: 'phone',            headerName: 'Phone' },
  { field: 'bgCheckValid',     headerName: 'Background Check', exportValue: r => r.bgCheckValid     ? 'Yes' : 'No' },
  { field: 'passportComplete', headerName: 'Passport Complete', exportValue: r => r.passportComplete ? 'Yes' : 'No' },
  { field: 'paymentStatus',    headerName: 'Balance' },
];

// ─── Main component ──────────────────────────────────────────────────────────

const TeamLeaderView = () => {
  const { eventId, teamName } = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const decodedTeamName = decodeURIComponent(teamName || '');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const resp = await axiosPrivate.get(
        `/api/my-reserved/team-view?eventId=${encodeURIComponent(eventId)}&teamName=${encodeURIComponent(decodedTeamName)}`
      );
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load team data.');
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, eventId, decodedTeamName]);

  useEffect(() => { load(); }, [load]);


  const columns = [
    {
      field: 'name', headerName: 'Name', flex: 1.4, minWidth: 180,
      renderCell: (p) => {
                
        const src      = resolvePhoto(p.row.photo);
        const isTeamLeader = p.row.isTeamLeader;
        const initials = (p.value || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
        // "You" comes from the API (the viewer's own record), not a user-id
        // match, since group members all share the leader's user id.
        const isMe     = !!p.row.isYou;

        if(isTeamLeader){
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={src || undefined}
              sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700,
                    bgcolor: '#1565c0', flexShrink: 0 }}
            >
              {!src && initials}
            </Avatar>
            <span>{p.value}</span>
            {isMe && (
              <Chip label="You" size="small" sx={{
                ml: 0.5, bgcolor: '#e8eaf6', color: '#3949ab',
                fontWeight: 700, fontSize: 10, height: 18,
                '& .MuiChip-label': { px: '6px' }
              }} />
            )}
          </Box>
        )} else {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{p.value}</span>
          </Box>
          )

          
        };
      }
    },
    { field: 'registrationType', headerName: 'Registration Type', flex: 1, minWidth: 140 },
    {
      field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180,
      renderCell: (p) => p.value
        ? <a href={`mailto:${p.value}`} onClick={e => e.stopPropagation()}
            style={{ color: '#1565c0', textDecoration: 'none' }}>{p.value}</a>
        : ''
    },
    { field: 'phone', headerName: 'Phone', flex: 0.8, minWidth: 120 },
    {
      field: 'bgCheckValid', headerName: 'Background Check', flex: 0.8, minWidth: 140,
      renderCell: (p) => p.row.isTeamLeader ? p.value ? <YesChip /> : <NoChip /> : "-",
    },
    {
      field: 'passportComplete', headerName: 'Passport Complete', flex: 0.8, minWidth: 140,
      renderCell: (p) => p.row.isTeamLeader ? p.value ? <YesChip /> : <NoChip /> : "-",
    },
    {
  field: 'paymentStatus', headerName: 'Balance', flex: 0.9, minWidth: 140,
  // Balance sits on the leader's order; members don't owe individually, so show
  // the payment/balance only on the leader row (blank for members).
  renderCell: (p) => p.row.isTeamLeader ? <PaymentChip status={p.value} balance={Number(p.row.balance)} /> : '',
},
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 300, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading team data…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: { xs: 2, md: 3 }, py: 3 }}>
          <Typography variant="h5" fontWeight={700}>{decodedTeamName || 'Team View'}</Typography>
        </Box>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
          <Button startIcon={<BackIcon />} variant="outlined" onClick={() => navigate('/myreserved')}
            sx={{ mb: 2, textTransform: 'none', borderColor: '#bdbdbd', color: 'text.primary' }}>
            Back
          </Button>
          <Typography color="error">{error}</Typography>
        </Box>
      </Box>
    );
  }

  const { event, teamName: tName, summary, members } = data || {};
  //console.log('Summary: ',data);
  const eventLabel = [event?.title, event?.location].filter(Boolean).join(' · ');
 const startDate = event?.startDate
  ? new Date(event.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '';

const endDate = event?.endDate
  ? new Date(event.endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '';


const dateRangeLabel = startDate && endDate && startDate !== endDate
  ? `${startDate} – ${endDate}`
  : startDate;

  return (
    <Box>

      {/* Blue event header — full width, no rounding */}
      <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: { xs: 2, md: 3 }, py: 3, mb: 0 }}>
        <Typography variant="h5" fontWeight={700}>{tName || decodedTeamName}</Typography>
        {eventLabel && (
  <Typography variant="body2" sx={{ opacity: .85, mt: 0.5 }}>
    {eventLabel}{dateRangeLabel ? ` · ${dateRangeLabel}` : ''}
  </Typography>
)}
      </Box>

      {/* Back button — below the header */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 0 }}>
        <Button
          startIcon={<BackIcon />}
          variant="outlined"
          onClick={() => navigate('/myreserved')}
          sx={{ textTransform: 'none', borderColor: '#bdbdbd', color: 'text.primary',
            '&:hover': { borderColor: '#9e9e9e', bgcolor: '#f5f5f5' } }}
        >
          Back
        </Button>
      </Box>

      {/* Stat cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, mt: 2, px: { xs: 2, md: 3 } }}>
        <StatCard label="Members"      value={summary?.totalMembers} />
        <StatCard label="Paid"         value={summary?.paidCount} />
        <StatCard label="Balance Due"  value={summary?.balanceDue} />
        <StatCard label="Days Until Trip"
          value={summary?.daysUntilTrip !== null ? summary?.daysUntilTrip : '–'} />
      </Stack>

      {/* Team members table */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', mx: { xs: 2, md: 3 }, mb: 3 }}>

        {/* Table toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa'
        }}>
          <Typography variant="subtitle1" fontWeight={600}>Team members</Typography>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Export CSV" arrow>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => exportCsv(members, EXPORT_COLS, `${tName || 'team'}-members.csv`)}
                sx={{ textTransform: 'none', borderColor: '#bdbdbd', color: 'text.primary',
                  '&:hover': { borderColor: '#9e9e9e', bgcolor: '#f5f5f5' } }}
              >
                Export CSV
              </Button>
            </Tooltip>
            <Tooltip title="Export Excel" arrow>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => exportExcel(members, EXPORT_COLS, `${tName || 'team'}-members.xls`)}
                sx={{ textTransform: 'none', borderColor: '#bdbdbd', color: 'text.primary',
                  '&:hover': { borderColor: '#9e9e9e', bgcolor: '#f5f5f5' } }}
              >
                Export Excel
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        <DataGrid
          rows={members || []}
          columns={columns}
          getRowId={r => r.id}
          autoHeight
          disableSelectionOnClick
          disableMultipleSelection
          disableColumnFilter
          disableDensitySelector
          hideFooterPagination
          hideFooter={!members || members.length <= 10}
          pageSize={100}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f5f5', fontWeight: 700 },
            '& .MuiDataGrid-row:hover': { bgcolor: '#f0f4ff' },
          }}
        />

        <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <Typography variant="body2" color="text.secondary">
            {summary?.totalMembers} member{summary?.totalMembers !== 1 ? 's' : ''}
            &nbsp;·&nbsp; {summary?.paidCount} paid
            &nbsp;·&nbsp; {summary?.balanceDue} balance due
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default TeamLeaderView;
